/**
 * Pure operations on the local sync outbox — an append-only queue of
 * immutable events awaiting upload to the backend.
 *
 * Idempotency is by event `id` (a UUID): appending an event whose id is
 * already queued is a no-op, so retries, double-commits, and replays never
 * duplicate. The outbox is a plain array; callers own persistence (it will
 * live inside the progress slice once the sync layer is switched on).
 *
 * Every function is pure: inputs are never mutated; a new array is returned
 * when the contents change.
 */

function asList(outbox) {
  return Array.isArray(outbox) ? outbox : [];
}

/**
 * Append an event unless one with the same id is already queued (dedupe).
 * Invalid events (missing string id) are ignored. Returns the same array
 * reference when nothing changes, so callers can cheaply detect a no-op.
 */
export function appendEvent(outbox, event) {
  const list = asList(outbox);
  if (!event || typeof event.id !== 'string' || event.id.length === 0) {
    return list;
  }
  if (list.some((e) => e && e.id === event.id)) {
    return list;
  }
  return [...list, event];
}

/**
 * Whether an event id is currently queued.
 */
export function hasEvent(outbox, id) {
  return asList(outbox).some((e) => e && e.id === id);
}

/**
 * The next batch to push, capped at `max`. Order is preserved (FIFO).
 */
export function pendingBatch(outbox, max = 50) {
  const list = asList(outbox);
  if (!Number.isInteger(max) || max <= 0) return [];
  return list.slice(0, max);
}

/**
 * Drop every event the server acknowledged. `ackedIds` is a list of event ids.
 */
export function removeAcked(outbox, ackedIds) {
  const list = asList(outbox);
  const acked = new Set(Array.isArray(ackedIds) ? ackedIds : []);
  if (acked.size === 0) return list;
  return list.filter((e) => !(e && acked.has(e.id)));
}

/**
 * Number of queued events.
 */
export function outboxSize(outbox) {
  return asList(outbox).length;
}
